import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Loader2,
  Send,
  Building2,
  Check,
  CircleSlash,
  AlertCircle,
  AlertTriangle,
  X,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { discountToApr } from "@/lib/negotiation/terms";
import { useNegotiationCampaigns } from "@/hooks/useNegotiationCampaigns";
import { useNegotiations } from "@/hooks/useNegotiations";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { useSettings } from "@/hooks/useSettings";
import type { CampaignStartResult } from "@/hooks/useNegotiationCampaigns";

export interface CampaignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vendor names to show as one-click suggestions (operator can add with a single click). May be empty. */
  suggestedVendorNames: string[];
  /** Every vendor name available, for adding vendors from scratch via search. */
  allVendorNames: string[];
  /**
   * Vendor names that should already be in the working list when the dialog opens
   * (e.g. the operator's checkbox selection in VendorsTable). When non-empty, the
   * dialog starts with these vendors pre-selected instead of a blank list.
   * Defaults to [].
   */
  preselectedVendorNames?: string[];
  /**
   * Default target discount % to pre-fill the field. When provided, saves the
   * operator from retyping the same value for every campaign.
   */
  defaultTargetDiscountPct?: number | null;
  /**
   * Default "pay within N days" to pre-fill the field.
   */
  defaultTargetDaysEarly?: number | null;
  /**
   * Default floor discount % (guardrails section).
   */
  defaultFloorDiscountPct?: number | null;
  /**
   * Default negotiation tone. Pre-fills the tone textarea.
   */
  defaultTone?: string | null;
  /**
   * Vendor names that already have an active (non-terminal) negotiation thread.
   * When a vendor in this set is added to the campaign, an amber caution warning
   * is shown inline. Launch is NOT blocked — the operator can proceed after seeing
   * the warning.
   */
  activeNegotiationVendors?: string[];
}

/** Simple RFC-5322 email check (good enough for a UI hint). */
function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** Step indicator shown at the top of the wizard. */
function StepIndicator({
  currentStep,
  totalSteps,
  labels,
}: {
  currentStep: number;
  totalSteps: number;
  labels: string[];
}) {
  return (
    <div className="flex items-center" aria-label="Wizard steps">
      {labels.map((label, idx) => {
        const stepNum = idx + 1;
        const isComplete = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        return (
          <div key={stepNum} className="flex items-center">
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors",
                  isComplete && "bg-primary text-primary-foreground",
                  isActive && "bg-primary text-primary-foreground ring-2 ring-primary/30",
                  !isComplete && !isActive && "bg-muted text-muted-foreground"
                )}
                aria-current={isActive ? "step" : undefined}
              >
                {isComplete ? <Check className="w-3 h-3" /> : stepNum}
              </div>
              <span
                className={cn(
                  "text-[9px] whitespace-nowrap leading-tight",
                  isActive ? "text-foreground font-medium" : "text-muted-foreground"
                )}
              >
                {label}
              </span>
            </div>
            {idx < totalSteps - 1 && (
              <div
                className={cn(
                  "h-px w-8 mx-1 mb-3 transition-colors",
                  isComplete ? "bg-primary" : "bg-border"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

const STEP_LABELS = ["Vendors", "Terms", "Message", "Launch"];
const TOTAL_STEPS = 4;

/**
 * CampaignDialog — multi-step wizard campaign builder.
 *
 * Step 1: Campaign name + vendor selection
 * Step 2: Terms (target discount, days early, derived APR, floor discount, tone)
 * Step 3: Message (subject template)
 * Step 4: Contacts & launch (send-from email, missing contact email gate, review, launch)
 *
 * All existing props, defaults/prefill behavior, and callers are preserved.
 */
export function CampaignDialog({
  open,
  onOpenChange,
  suggestedVendorNames = [],
  allVendorNames = [],
  preselectedVendorNames = [],
  defaultTargetDiscountPct = null,
  defaultTargetDaysEarly = null,
  defaultFloorDiscountPct = null,
  defaultTone = null,
  activeNegotiationVendors = [],
}: CampaignDialogProps) {
  const [currentStep, setCurrentStep] = useState(1);

  const [campaignName, setCampaignName] = useState("");
  const [result, setResult] = useState<CampaignStartResult | null>(null);
  // Map of vendor_name → draft email value entered by the user
  const [missingEmails, setMissingEmails] = useState<Record<string, string>>({});
  // Working list: starts empty; operators add vendors via suggestions or search
  const [workingVendors, setWorkingVendors] = useState<string[]>([]);
  // "Send from" address for this campaign
  const [fromEmail, setFromEmail] = useState("");

  const { startCampaign, isStartingCampaign } = useNegotiationCampaigns();
  const { createThread } = useNegotiations();
  const { contacts, upsertContact, isUpserting } = useVendorContacts();
  const { settings, updateSettings } = useSettings();

  // ── Campaign-wide negotiation defaults ────────────────────────────────────
  const [targetDiscount, setTargetDiscount] = useState(
    defaultTargetDiscountPct != null
      ? String(defaultTargetDiscountPct)
      : settings?.default_target_discount_pct != null
        ? String(settings.default_target_discount_pct)
        : ""
  );
  const [daysEarly, setDaysEarly] = useState(
    defaultTargetDaysEarly != null
      ? String(defaultTargetDaysEarly)
      : settings?.default_target_days_early != null
        ? String(settings.default_target_days_early)
        : ""
  );
  const [floorDiscount, setFloorDiscount] = useState(
    defaultFloorDiscountPct != null ? String(defaultFloorDiscountPct) : ""
  );
  const [tone, setTone] = useState(defaultTone ?? settings?.default_negotiation_tone ?? "");
  const [subjectTemplate, setSubjectTemplate] = useState(
    "Early Payment Discount Proposal — {vendor_name}"
  );
  const [guardrailsOpen, setGuardrailsOpen] = useState(false);

  // Add-vendor search state
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Keep a ref to the latest suggestedVendorNames so handleClose can read the
  // current value without stale-closure issues.
  const suggestedVendorNamesRef = useRef(suggestedVendorNames);
  useEffect(() => {
    suggestedVendorNamesRef.current = suggestedVendorNames;
  });

  // Keep a ref to preselectedVendorNames to avoid stale closure in the open guard.
  const preselectedVendorNamesRef = useRef(preselectedVendorNames);
  useEffect(() => {
    preselectedVendorNamesRef.current = preselectedVendorNames;
  });

  // Reset working state each time the dialog opens.
  // When preselectedVendorNames is non-empty the working list is seeded from it
  // (operator's checkbox selection); otherwise it starts blank.
  // Guarded on the closed→open transition so mid-edit changes aren't wiped
  // by re-renders while the dialog is open.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setCurrentStep(1);
      setWorkingVendors(
        preselectedVendorNamesRef.current.length > 0 ? [...preselectedVendorNamesRef.current] : []
      );
      setMissingEmails({});
      setResult(null);
      setSearchQuery("");
      setShowSearch(false);
      setFromEmail(settings?.negotiation_from_email ?? "");
      // Props take priority; fall back to global settings defaults when props are null (KAY-116)
      setTargetDiscount(
        defaultTargetDiscountPct != null
          ? String(defaultTargetDiscountPct)
          : settings?.default_target_discount_pct != null
            ? String(settings.default_target_discount_pct)
            : ""
      );
      setDaysEarly(
        defaultTargetDaysEarly != null
          ? String(defaultTargetDaysEarly)
          : settings?.default_target_days_early != null
            ? String(settings.default_target_days_early)
            : ""
      );
      setFloorDiscount(defaultFloorDiscountPct != null ? String(defaultFloorDiscountPct) : "");
      setTone(defaultTone ?? settings?.default_negotiation_tone ?? "");
      setSubjectTemplate("Early Payment Discount Proposal — {vendor_name}");
      setGuardrailsOpen(false);
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settings?.negotiation_from_email, settings?.default_negotiation_tone]);

  // Build a quick map for O(1) lookup
  const contactMap = useMemo(() => new Map(contacts.map((c) => [c.vendor_name, c])), [contacts]);

  // Vendors in the working list that are missing a contact email
  const vendorsMissingEmail = useMemo(
    () =>
      workingVendors.filter((name) => {
        const c = contactMap.get(name);
        return !c?.supplier_email;
      }),
    [workingVendors, contactMap]
  );

  // True if every vendor in the working list either has an existing email or
  // the user has entered a valid draft email.
  const allVendorsHaveEmail = useMemo(
    () =>
      vendorsMissingEmail.every((name) => {
        const val = (missingEmails[name] ?? "").trim();
        return val && isValidEmail(val);
      }),
    [vendorsMissingEmail, missingEmails]
  );

  const isBusy = isStartingCampaign || isUpserting;

  // Working set for O(1) membership checks
  const workingSet = useMemo(() => new Set(workingVendors), [workingVendors]);

  // O(1) lookup for active-negotiation vendors
  const activeNegotiationSet = useMemo(
    () => new Set(activeNegotiationVendors),
    [activeNegotiationVendors]
  );

  // Vendors in the current working list that already have an active negotiation
  const warnedVendors = useMemo(
    () => workingVendors.filter((name) => activeNegotiationSet.has(name)),
    [workingVendors, activeNegotiationSet]
  );

  // Suggested vendors not yet in the working list (shown as quick-add chips)
  const quickAddSuggestions = useMemo(
    () => suggestedVendorNames.filter((name) => !workingSet.has(name)),
    [suggestedVendorNames, workingSet]
  );

  // Vendors available to add via free-search (not already in working list)
  const addableVendors = useMemo(
    () => allVendorNames.filter((name) => !workingSet.has(name)),
    [allVendorNames, workingSet]
  );

  // Filtered by search query
  const filteredAddable = useMemo(() => {
    if (!searchQuery.trim()) return addableVendors;
    const q = searchQuery.toLowerCase();
    return addableVendors.filter((name) => name.toLowerCase().includes(q));
  }, [addableVendors, searchQuery]);

  function handleRemoveVendor(name: string) {
    setWorkingVendors((prev) => prev.filter((v) => v !== name));
    setMissingEmails((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }

  function handleAddVendor(name: string) {
    setWorkingVendors((prev) => [...prev, name]);
    setSearchQuery("");
    setShowSearch(false);
  }

  function handleClearAll() {
    setWorkingVendors([]);
    setMissingEmails({});
  }

  const handleClose = useCallback(() => {
    onOpenChange(false);
    // Reset state after close animation.
    setTimeout(() => {
      setCurrentStep(1);
      setCampaignName("");
      setResult(null);
      setMissingEmails({});
      setSearchQuery("");
      setShowSearch(false);
      setWorkingVendors([]);
      setFromEmail("");
      setTargetDiscount("");
      setDaysEarly("");
      setFloorDiscount("");
      setTone("");
      setSubjectTemplate("Early Payment Discount Proposal — {vendor_name}");
      setGuardrailsOpen(false);
    }, 200);
  }, [onOpenChange]);

  async function handleConfirm() {
    if (!campaignName.trim()) return;

    // Persist the "Send from" address if it has been set/changed
    const trimmedFromEmail = fromEmail.trim();
    if (trimmedFromEmail && trimmedFromEmail !== (settings?.negotiation_from_email ?? "")) {
      await updateSettings({ negotiation_from_email: trimmedFromEmail });
    }

    // Persist any valid emails the user entered
    const upsertPromises = vendorsMissingEmail
      .filter((name) => {
        const val = (missingEmails[name] ?? "").trim();
        return val && isValidEmail(val);
      })
      .map((name) => upsertContact(name, { supplier_email: missingEmails[name].trim() }));

    if (upsertPromises.length > 0) {
      await Promise.all(upsertPromises);
    }

    // Build numeric terms from the form state
    const targetDiscountNum = targetDiscount ? Number(targetDiscount) : undefined;
    const daysEarlyNum = daysEarly ? Number(daysEarly) : undefined;
    const floorDiscountNum = floorDiscount ? Number(floorDiscount) : undefined;
    const floorAprNum =
      floorDiscountNum != null && daysEarlyNum != null
        ? discountToApr(floorDiscountNum, daysEarlyNum)
        : undefined;
    const targetAprNum =
      targetDiscountNum != null && daysEarlyNum != null
        ? discountToApr(targetDiscountNum, daysEarlyNum)
        : undefined;

    const res = await startCampaign(
      workingVendors,
      {
        campaign_name: campaignName.trim(),
        subject_template:
          subjectTemplate.trim() || "Early Payment Discount Proposal — {vendor_name}",
        // Campaign-wide negotiation defaults
        ...(targetDiscountNum != null ? { target_discount_pct: targetDiscountNum } : {}),
        ...(daysEarlyNum != null ? { target_days_early: daysEarlyNum } : {}),
        ...(floorDiscountNum != null ? { floor_discount_pct: floorDiscountNum } : {}),
        ...(floorAprNum != null ? { floor_apr: floorAprNum } : {}),
        ...(targetAprNum != null ? { target_apr: targetAprNum } : {}),
        ...(tone.trim() ? { tone: tone.trim() } : {}),
      },
      createThread
    );

    setResult(res);
  }

  const isDone = result !== null;

  // fromEmail is invalid when: something was typed but it's not a valid email
  const fromEmailValue = fromEmail.trim();
  const isFromEmailInvalid = fromEmailValue.length > 0 && !isValidEmail(fromEmailValue);

  // Subject template validation: must contain {vendor_name}
  const subjectTemplateTrimmed = subjectTemplate.trim();
  const isSubjectTemplateInvalid =
    subjectTemplateTrimmed.length > 0 && !subjectTemplateTrimmed.includes("{vendor_name}");

  // Launch is disabled when: no name, busy, empty list, invalid from-email, or any vendor still lacks email
  const isLaunchDisabled =
    !campaignName.trim() ||
    isBusy ||
    workingVendors.length === 0 ||
    !allVendorsHaveEmail ||
    isFromEmailInvalid ||
    isSubjectTemplateInvalid ||
    // Also disable if any entered email is syntactically invalid
    vendorsMissingEmail.some((name) => {
      const val = (missingEmails[name] ?? "").trim();
      return val && !isValidEmail(val);
    });

  const showLaunchHint =
    !isBusy && campaignName.trim().length > 0 && workingVendors.length > 0 && !allVendorsHaveEmail;

  // ── Per-step validation (Next button enabled check) ───────────────────────
  const step1Valid = campaignName.trim().length > 0 && workingVendors.length > 0;
  // step2: all optional fields — always valid
  const step3Valid = !isSubjectTemplateInvalid; // valid or empty → allow Next

  function handleNext() {
    if (currentStep < TOTAL_STEPS) setCurrentStep((s) => s + 1);
  }

  function handleBack() {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-lg flex flex-col max-h-[88vh] overflow-hidden gap-2 p-4 sm:p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-base font-semibold">
            {isDone ? "Campaign launched" : "Create negotiation campaign"}
          </DialogTitle>
          {!isDone && (
            <div className="pt-2">
              <StepIndicator
                currentStep={currentStep}
                totalSteps={TOTAL_STEPS}
                labels={STEP_LABELS}
              />
            </div>
          )}
        </DialogHeader>

        {!isDone ? (
          <div className="flex-1 overflow-y-auto min-h-0 py-2 px-0.5">
            {/* ── Step 1: Campaign name + vendor selection ──────────────────── */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label
                    htmlFor="cd-campaign-name"
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Campaign name *
                  </Label>
                  <Input
                    id="cd-campaign-name"
                    placeholder="e.g. Q3 Early Pay Outreach"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    className="h-8 text-xs"
                    disabled={isBusy}
                  />
                </div>

                {/* Suggested quick-adds */}
                {quickAddSuggestions.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">Suggested</span>
                    <div className="flex flex-wrap gap-1.5">
                      {quickAddSuggestions.map((name) => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => handleAddVendor(name)}
                          disabled={isBusy}
                          aria-label={`Add suggested vendor ${name}`}
                          className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/30 px-2 py-0.5 text-[11px] text-foreground hover:bg-accent hover:border-accent-foreground/20 disabled:pointer-events-none disabled:opacity-50 transition-colors"
                        >
                          <Plus className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          <span className="truncate max-w-[140px]">{name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vendor list */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Vendors ({workingVendors.length})
                    </span>
                    {workingVendors.length > 0 && (
                      <button
                        type="button"
                        onClick={handleClearAll}
                        disabled={isBusy}
                        className="text-[10px] text-muted-foreground hover:text-destructive underline underline-offset-2 disabled:pointer-events-none disabled:opacity-50"
                        aria-label="Clear all vendors"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {workingVendors.length > 0 ? (
                    <ul className="max-h-40 overflow-y-auto space-y-0.5 rounded-md border border-border/60 p-2 bg-muted/20">
                      {workingVendors.map((name) => {
                        const isActive = activeNegotiationSet.has(name);
                        return (
                          <li
                            key={name}
                            className="flex items-center gap-2 text-xs text-foreground"
                          >
                            <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="truncate flex-1">{name}</span>
                            {isActive && (
                              <span
                                title="Already in an active negotiation — you can still proceed"
                                aria-label={`${name} already has an active negotiation`}
                                className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 flex-shrink-0"
                              >
                                <AlertTriangle className="w-2.5 h-2.5" />
                                Active
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                              onClick={() => handleRemoveVendor(name)}
                              disabled={isBusy}
                              aria-label={`Remove ${name} from campaign`}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-[11px] text-muted-foreground rounded-md border border-border/60 p-3 bg-muted/20 text-center">
                      No vendors added yet. Search below to add vendors.
                    </p>
                  )}
                </div>

                {/* Active negotiation warning banner (non-blocking) */}
                {warnedVendors.length > 0 && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-700 dark:text-amber-400">
                      {warnedVendors.length === 1
                        ? `${warnedVendors[0]} already has an active negotiation. You can still proceed.`
                        : `${warnedVendors.length} vendors already have active negotiations. You can still proceed.`}
                    </p>
                  </div>
                )}

                {/* Add vendor search */}
                <div className="space-y-1.5">
                  {!showSearch ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-7 text-xs gap-1.5"
                      onClick={() => setShowSearch(true)}
                      disabled={isBusy || addableVendors.length === 0}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add vendor
                    </Button>
                  ) : (
                    <div className="space-y-1">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          placeholder="Search vendors…"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="h-7 text-xs pl-7 pr-7"
                          disabled={isBusy}
                          autoFocus
                          aria-label="Search vendors to add"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setShowSearch(false);
                            setSearchQuery("");
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          aria-label="Close vendor search"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {filteredAddable.length > 0 ? (
                        <ul className="max-h-28 overflow-y-auto rounded-md border border-border/60 bg-popover shadow-sm">
                          {filteredAddable.map((name) => (
                            <li key={name}>
                              <button
                                type="button"
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-accent hover:text-accent-foreground rounded-sm"
                                onClick={() => handleAddVendor(name)}
                                aria-label={`Add ${name} to campaign`}
                              >
                                <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <span className="truncate">{name}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[11px] text-muted-foreground px-2 py-1.5">
                          {searchQuery.trim()
                            ? "No matching vendors found."
                            : "All vendors already added."}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Step 2: Terms ─────────────────────────────────────────────── */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <p className="text-xs font-medium text-muted-foreground">
                  Negotiation defaults (applied to all vendors)
                </p>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="cd-target-discount" className="text-xs">
                      Target Discount %
                    </Label>
                    <Input
                      id="cd-target-discount"
                      value={targetDiscount}
                      onChange={(e) => setTargetDiscount(e.target.value)}
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="2.00"
                      className="h-9 text-sm"
                      disabled={isBusy}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="cd-days-early" className="text-xs">
                      Early Pay Days
                    </Label>
                    <Input
                      id="cd-days-early"
                      value={daysEarly}
                      onChange={(e) => setDaysEarly(e.target.value)}
                      type="number"
                      min="1"
                      placeholder="20"
                      className="h-9 text-sm"
                      disabled={isBusy}
                    />
                  </div>
                </div>

                <Collapsible open={guardrailsOpen} onOpenChange={setGuardrailsOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Toggle guardrails"
                    >
                      <ChevronDown
                        className={cn(
                          "w-3.5 h-3.5 transition-transform",
                          guardrailsOpen && "rotate-180"
                        )}
                      />
                      Guardrails (advanced)
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="cd-floor-discount" className="text-xs">
                        Floor Discount % (min acceptable)
                      </Label>
                      <Input
                        id="cd-floor-discount"
                        value={floorDiscount}
                        onChange={(e) => setFloorDiscount(e.target.value)}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="1.00"
                        className="h-9 text-sm"
                        disabled={isBusy}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                <div className="space-y-1.5">
                  <Label htmlFor="cd-tone" className="text-xs">
                    Negotiation Tone
                  </Label>
                  <Textarea
                    id="cd-tone"
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    placeholder="Professional and collaborative. Emphasise long-term partnership."
                    rows={3}
                    className="text-sm resize-none"
                    disabled={isBusy}
                  />
                </div>
              </div>
            )}

            {/* ── Step 3: Message ────────────────────────────────────────────── */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cd-subject-template" className="text-xs font-medium">
                    Subject template
                  </Label>
                  <Input
                    id="cd-subject-template"
                    value={subjectTemplate}
                    onChange={(e) => setSubjectTemplate(e.target.value)}
                    placeholder="Early Payment Discount Proposal — {vendor_name}"
                    className={`h-9 text-sm ${
                      isSubjectTemplateInvalid
                        ? "border-destructive focus-visible:ring-destructive"
                        : ""
                    }`}
                    disabled={isBusy}
                  />
                  {isSubjectTemplateInvalid ? (
                    <p className="text-[10px] text-destructive">
                      Must include <code>{"{vendor_name}"}</code> placeholder
                    </p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground/60">
                      Use <code>{"{vendor_name}"}</code> — it&apos;s replaced per vendor
                    </p>
                  )}
                </div>

                {/* Brief preview of campaign so far */}
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    Campaign so far
                  </p>
                  <div className="text-xs space-y-0.5 text-foreground">
                    <p>
                      <span className="text-muted-foreground">Name:</span> {campaignName}
                    </p>
                    <p>
                      <span className="text-muted-foreground">Vendors:</span>{" "}
                      {workingVendors.length}
                    </p>
                    {targetDiscount && (
                      <p>
                        <span className="text-muted-foreground">Target:</span> {targetDiscount}%
                        {daysEarly && ` in ${daysEarly} days`}
                      </p>
                    )}
                    {tone && (
                      <p className="truncate">
                        <span className="text-muted-foreground">Tone:</span> {tone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 4: Contacts & launch ─────────────────────────────────── */}
            {currentStep === 4 && (
              <div className="space-y-4">
                {/* Send from email */}
                <div className="space-y-1.5">
                  <Label
                    htmlFor="cd-from-email"
                    className="text-xs font-medium text-muted-foreground"
                  >
                    Send from
                  </Label>
                  <Input
                    id="cd-from-email"
                    type="email"
                    placeholder="negotiations@yourcompany.com"
                    value={fromEmail}
                    onChange={(e) => setFromEmail(e.target.value)}
                    className={`h-9 text-xs ${
                      isFromEmailInvalid ? "border-destructive focus-visible:ring-destructive" : ""
                    }`}
                    disabled={isBusy}
                    aria-label="Send from email address"
                  />
                  {isFromEmailInvalid && (
                    <p className="text-[10px] text-destructive">Enter a valid email address</p>
                  )}
                </div>

                {/* Missing contact email section */}
                {vendorsMissingEmail.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                        Missing contact info ({vendorsMissingEmail.length})
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {vendorsMissingEmail.map((name) => {
                        const val = missingEmails[name] ?? "";
                        const hasInput = val.trim().length > 0;
                        const isInvalid = hasInput && !isValidEmail(val);
                        return (
                          <li key={name} className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs text-foreground truncate flex-1">
                                {name}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
                                onClick={() => handleRemoveVendor(name)}
                                disabled={isBusy}
                                aria-label={`Remove ${name} from campaign`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <Input
                              type="email"
                              placeholder="supplier@example.com"
                              value={val}
                              onChange={(e) =>
                                setMissingEmails((prev) => ({
                                  ...prev,
                                  [name]: e.target.value,
                                }))
                              }
                              className={`h-7 text-xs ml-5 ${
                                isInvalid ? "border-destructive focus-visible:ring-destructive" : ""
                              }`}
                              disabled={isBusy}
                              aria-label={`Contact email for ${name}`}
                            />
                            {isInvalid && (
                              <p className="text-[10px] text-destructive ml-5">
                                Enter a valid email address
                              </p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {showLaunchHint && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400">
                    Add a contact email or remove each vendor with missing info to enable launch.
                  </p>
                )}

                {/* Active negotiation warning on launch step (non-blocking) */}
                {warnedVendors.length > 0 && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="space-y-0.5">
                      <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">
                        {warnedVendors.length === 1
                          ? "1 vendor already has an active negotiation"
                          : `${warnedVendors.length} vendors already have active negotiations`}
                      </p>
                      <ul className="space-y-0 list-disc list-inside">
                        {warnedVendors.map((name) => (
                          <li key={name} className="text-[10px] text-amber-600 dark:text-amber-400">
                            {name}
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-amber-600 dark:text-amber-400">
                        You can still launch — this is a caution only.
                      </p>
                    </div>
                  </div>
                )}

                {/* Review summary */}
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    Ready to launch
                  </p>
                  <div className="text-xs space-y-0.5 text-foreground">
                    <p>
                      <span className="text-muted-foreground">Campaign:</span> {campaignName}
                    </p>
                    <p>
                      <span className="text-muted-foreground">Vendors:</span>{" "}
                      {workingVendors.length}
                    </p>
                    {targetDiscount && (
                      <p>
                        <span className="text-muted-foreground">Target:</span> {targetDiscount}%
                        {daysEarly && ` in ${daysEarly} days`}
                      </p>
                    )}
                    <p>
                      <span className="text-muted-foreground">Subject:</span>{" "}
                      {subjectTemplate || "Early Payment Discount Proposal — {vendor_name}"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Summary view (post-launch)
          <div className="space-y-4 py-2 flex-1 overflow-y-auto min-h-0">
            <div className="flex gap-4">
              <div className="flex items-center gap-1.5 text-xs text-success">
                <Check className="w-4 h-4" />
                <span className="font-semibold">{result.started.length} started</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CircleSlash className="w-4 h-4" />
                <span>{result.skipped.length} skipped</span>
              </div>
            </div>

            {result.started.length > 0 && (
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">Started</p>
                <ul className="space-y-0.5 max-h-28 overflow-y-auto">
                  {result.started.map((name) => (
                    <li key={name} className="text-xs text-foreground flex items-center gap-1.5">
                      <Check className="w-3 h-3 text-success flex-shrink-0" />
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.skipped.length > 0 && (
              <div className="space-y-1">
                <p className="text-[11px] font-medium text-muted-foreground">Skipped</p>
                <ul className="space-y-0.5 max-h-28 overflow-y-auto">
                  {result.skipped.map(({ vendor_name, reason }) => (
                    <li
                      key={vendor_name}
                      className="text-xs text-muted-foreground flex items-center gap-1.5"
                    >
                      <CircleSlash className="w-3 h-3 flex-shrink-0" />
                      <span className="font-medium text-foreground">{vendor_name}</span>
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1 py-0 border-border/60 text-muted-foreground"
                      >
                        {reason}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="shrink-0 gap-2">
          {!isDone ? (
            <>
              {/* Left side: Cancel (step 1) or Back (steps 2-4) */}
              <div className="flex-1 flex items-center">
                {currentStep === 1 ? (
                  <Button variant="outline" size="sm" onClick={handleClose} disabled={isBusy}>
                    Cancel
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleBack} disabled={isBusy}>
                    Back
                  </Button>
                )}
              </div>

              {/* Right side: Next or Launch */}
              {currentStep < TOTAL_STEPS ? (
                <Button
                  size="sm"
                  onClick={handleNext}
                  disabled={
                    isBusy ||
                    (currentStep === 1 && !step1Valid) ||
                    (currentStep === 3 && !step3Valid)
                  }
                  className="gap-1.5"
                  aria-label={`Next step (${STEP_LABELS[currentStep]})`}
                >
                  {STEP_LABELS[currentStep]}
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={isLaunchDisabled}
                  className="gap-1.5"
                >
                  {isBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  Launch campaign
                </Button>
              )}
            </>
          ) : (
            <Button size="sm" onClick={handleClose}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
